import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DailySpendEntry } from './types';
import { Period, weekStart } from './PeriodSelector';

const C = {
  needs: '#159b72',
  wants: '#EC4899',
  savings: '#3B82F6',
  red: '#ff454d',
  muted: '#78869a',
};

export interface SpendingBarChartProps {
  entries: DailySpendEntry[];
  period: Period;
  refDate: Date;
  monthlyTarget: number;
  customFrom?: string;
  customTo?: string;
}

const SpendingBarChart = ({
  entries,
  period,
  refDate,
  monthlyTarget,
  customFrom,
  customTo,
}: SpendingBarChartProps) => {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const bars = useMemo<
    {
      label: string;
      needs: number;
      wants: number;
      savings: number;
      total: number;
      target: number;
      isFuture: boolean;
    }[]
  >(() => {
    if (!entries || entries.length === 0) return [];

    const daysInMonth = new Date(
      refDate.getFullYear(),
      refDate.getMonth() + 1,
      0,
    ).getDate();
    const dailyTarget = (monthlyTarget || 0) / (daysInMonth || 30);

    if (period === 'day') {
      const todayIso = today.toISOString().slice(0, 10);
      const e = entries.find((x) => x.date === todayIso) ?? {
        needs: 0,
        wants: 0,
        savings: 0,
        total: 0,
        dailyTarget,
      };
      return [
        {
          label: 'Today',
          needs: e.needs,
          wants: e.wants,
          savings: e.savings,
          total: e.total,
          target: e.dailyTarget || dailyTarget,
          isFuture: false,
        },
      ];
    }

    if (period === 'week') {
      const ws = weekStart(refDate);
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(ws);
        d.setDate(ws.getDate() + i);
        const iso = d.toISOString().slice(0, 10);
        const e = entries.find((x) => x.date === iso);
        return {
          label: d.toLocaleDateString('en-GB', { weekday: 'short' }),
          needs: e?.needs ?? 0,
          wants: e?.wants ?? 0,
          savings: e?.savings ?? 0,
          total: e?.total ?? 0,
          target: e?.dailyTarget ?? dailyTarget,
          isFuture: d > today,
        };
      });
    }

    if (period === 'month') {
      return Array.from({ length: daysInMonth }, (_, i) => {
        const d = new Date(refDate.getFullYear(), refDate.getMonth(), i + 1);
        const iso = d.toISOString().slice(0, 10);
        const e = entries.find((x) => x.date === iso);
        return {
          label: String(i + 1),
          needs: e?.needs ?? 0,
          wants: e?.wants ?? 0,
          savings: e?.savings ?? 0,
          total: e?.total ?? 0,
          target: e?.dailyTarget ?? dailyTarget,
          isFuture: d > today,
        };
      });
    }

    // custom — daily bars for the selected range
    if (period === 'custom' && customFrom && customTo) {
      const from = new Date(customFrom + 'T00:00:00');
      const to = new Date(customTo + 'T00:00:00');
      const count = Math.max(
        Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1,
        1,
      );
      return Array.from({ length: count }, (_, i) => {
        const d = new Date(from);
        d.setDate(from.getDate() + i);
        const iso = d.toISOString().slice(0, 10);
        const e = entries.find((x) => x.date === iso);
        return {
          label: d.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
          }),
          needs: e?.needs ?? 0,
          wants: e?.wants ?? 0,
          savings: e?.savings ?? 0,
          total: e?.total ?? 0,
          target: e?.dailyTarget ?? dailyTarget,
          isFuture: d > today,
        };
      });
    }

    // year — aggregate per month
    return Array.from({ length: 12 }, (_, i) => {
      const monthEntries = entries.filter(
        (e) => new Date(e.date).getMonth() === i,
      );
      const d = new Date(refDate.getFullYear(), i, 1);
      return {
        label: d.toLocaleDateString('en-GB', { month: 'short' }),
        needs: monthEntries.reduce((s, e) => s + e.needs, 0),
        wants: monthEntries.reduce((s, e) => s + e.wants, 0),
        savings: monthEntries.reduce((s, e) => s + e.savings, 0),
        total: monthEntries.reduce((s, e) => s + e.total, 0),
        target: monthlyTarget,
        isFuture: d > today,
      };
    });
  }, [entries, period, refDate, monthlyTarget, customFrom, customTo, today]);

  if (bars.length === 0) {
    return (
      <Box
        sx={{
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No transaction data for this period
        </Typography>
      </Box>
    );
  }

  const maxVal = Math.max(...bars.map((b) => Math.max(b.total, b.target)), 1);
  const BAR_H = 100;
  const tooMany = bars.length > 14;

  return (
    <Box>
      {/* Legend */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 18,
              height: 2,
              borderTop: `2px dashed ${alpha(C.muted, 0.5)}`,
            }}
          />
          <Typography
            variant="caption"
            sx={{ color: C.muted, fontSize: '0.65rem' }}
          >
            Target
          </Typography>
        </Box>
        {[
          { label: 'Needs', color: C.needs },
          { label: 'Wants', color: C.wants },
          { label: 'Savings', color: C.savings },
        ].map(({ label, color }) => (
          <Box
            key={label}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '2px',
                bgcolor: color,
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: C.muted, fontSize: '0.65rem' }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Bar area */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: tooMany ? 0.25 : 0.75,
          height: BAR_H + 20,
          px: 0.5,
          overflowX: tooMany ? 'auto' : 'hidden',
        }}
      >
        {bars.map((bar, i) => {
          const totalH = Math.max(
            Math.round((bar.total / maxVal) * BAR_H),
            bar.total > 0 ? 2 : 0,
          );
          const targetH = Math.max(
            Math.round((bar.target / maxVal) * BAR_H),
            2,
          );
          const over =
            !bar.isFuture && bar.total > bar.target && bar.target > 0;
          const needsH = Math.round(
            (bar.needs / Math.max(bar.total, 1)) * totalH,
          );
          const wantsH = Math.round(
            (bar.wants / Math.max(bar.total, 1)) * totalH,
          );
          const savingsH = Math.max(totalH - needsH - wantsH, 0);

          return (
            <Box
              key={i}
              sx={{
                flex: tooMany ? '0 0 auto' : 1,
                minWidth: tooMany ? 5 : undefined,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: BAR_H,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'flex-end',
                }}
              >
                {/* Dashed target ghost */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: targetH,
                    border: `1.5px dashed ${alpha(C.muted, 0.35)}`,
                    borderRadius: '3px 3px 0 0',
                    pointerEvents: 'none',
                  }}
                />
                {/* Actual bar */}
                {bar.isFuture ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: targetH,
                      bgcolor: alpha(C.muted, 0.07),
                      border: `1px solid ${alpha(C.muted, 0.15)}`,
                      borderRadius: '3px 3px 0 0',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: totalH,
                      display: 'flex',
                      flexDirection: 'column-reverse',
                      borderRadius: '3px 3px 0 0',
                      overflow: 'hidden',
                    }}
                  >
                    {needsH > 0 && (
                      <Box
                        sx={{ height: needsH, bgcolor: over ? C.red : C.needs }}
                      />
                    )}
                    {wantsH > 0 && (
                      <Box
                        sx={{
                          height: wantsH,
                          bgcolor: over ? alpha(C.red, 0.75) : C.wants,
                        }}
                      />
                    )}
                    {savingsH > 0 && (
                      <Box
                        sx={{
                          height: savingsH,
                          bgcolor: over ? alpha(C.red, 0.5) : C.savings,
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
              {!tooMany && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.58rem',
                    color: C.muted,
                    mt: 0.4,
                    lineHeight: 1,
                  }}
                >
                  {bar.label}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SpendingBarChart;
